/**
 * BED-40785 / kuba/icon-settin-revert: OTA origin icons on calendar tiles are always on
 * (like legacy integration borders). The tag below is hidden from Personalizacja → Etykiety
 * and removed from default label patterns — restore SettingsForm + defaults to re-enable UI.
 */
import { differenceInCalendarDays } from 'date-fns'
import { ICalendarDimensions } from '../containers/BookingCalendar/Calendar/Calendar'
import logoAirbnb from '../assets/icons/logo Airbnb.png'
import logoBB from '../assets/icons/logo BB.png'
import logoBooking from '../assets/icons/logo Booking.png'
import logoICalendar from '../assets/icons/logo iCalendar.png'
import { imageCacheService } from './images/imageCache'

export const ORIGIN_ICON_TAG = '#Source.of.booking.origin#'
const ORIGIN_ICON_SIZE = 24
const ORIGIN_ICON_SIZE_SINGLE_DAY = 18

const LOGOS: Record<string, string> = {
  bookingcom: logoBooking,
  airbnb: logoAirbnb,
  icalendar: logoICalendar,
  fromWebsite: logoBB,
}

type OriginIconLayout = {
  channel?: string
  iconOnly: boolean
  iconSize: number
  iconX: number
  iconY: number
  inset: number
}

function isSet(v: any): boolean {
  if (v == null) return false
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return Number.isFinite(v) && v > 0

  const value = String(v).trim().toLowerCase()
  return (
    value !== '' &&
    value !== '0' &&
    value !== 'false' &&
    value !== 'null' &&
    value !== 'undefined' &&
    value !== 'nan'
  )
}

function resolveChannel(event: any): string | undefined {
  const source = event || {}

  if (
    isSet(source.bookingId) ||
    isSet(source.booking_id_reservation) ||
    isSet(source.booking_id) ||
    isSet(source.booking)
  ) {
    return 'bookingcom'
  }

  if (
    isSet(source.airbnbId) ||
    isSet(source.airbnb_id_reservation) ||
    isSet(source.airbnb_id) ||
    isSet(source.airbnb)
  ) {
    return 'airbnb'
  }

  if (
    isSet(source.iCalendarId) ||
    isSet(source.icalendar_id_reservation) ||
    isSet(source.icalendar_id) ||
    isSet(source.ical) ||
    isSet(source.i_calendar_id)
  ) {
    return 'icalendar'
  }

  if (
    isSet(source.fromWebsiteId) ||
    isSet(source.fromWebsite) ||
    isSet(source.from_website) ||
    isSet(source.reservationSystemId) ||
    isSet(source.reservation_system_id)
  ) {
    return 'fromWebsite'
  }
}

export function isSingleDayEvent(event: any): boolean {
  if (!event || !event.startDate || !event.finishDate) return false
  return differenceInCalendarDays(event.finishDate, event.startDate) <= 1
}

export function isOriginIconTag(label?: string | null): boolean {
  return Boolean(label?.includes(ORIGIN_ICON_TAG))
}

export function ensureOriginIconFirstInString(label?: string | null): string {
  if (!label || !isOriginIconTag(label)) return label || ''

  const rest = label.replace(ORIGIN_ICON_TAG, '').trim()
  return rest ? `${ORIGIN_ICON_TAG} ${rest}` : ORIGIN_ICON_TAG
}

export function getOriginIconLayout(
  constants: ICalendarDimensions,
  shape: string,
  x: number,
  y: number,
  width: number,
  height: number,
  event: any,
  enabled: boolean,
  iconCentered: boolean,
): OriginIconLayout {
  const empty: OriginIconLayout = {
    iconOnly: false,
    iconSize: 0,
    iconX: 0,
    iconY: 0,
    inset: 0,
  }

  const channel = enabled && resolveChannel(event)
  if (!channel) return empty

  const iconOnly = iconCentered
  const iconSize = isSingleDayEvent(event)
    ? ORIGIN_ICON_SIZE_SINGLE_DAY
    : ORIGIN_ICON_SIZE
  const padding = shape === 'diamond' ? 9 : 4
  const iconY = y + (height - iconSize) / 2

  const iconX = iconOnly
    ? (shape === 'diamond'
        ? x + (width + constants.EVENT_DIAMOND_SLOPE) / 2
        : x + width / 2) -
      iconSize / 2
    : shape === 'diamond'
    ? x +
      (constants.EVENT_DIAMOND_SLOPE * (height + iconSize)) / (2 * height) +
      padding
    : x + padding

  return {
    channel,
    iconOnly,
    iconSize,
    iconX,
    iconY,
    inset: iconOnly ? 0 : iconX - x + iconSize + 4,
  }
}

export function drawOriginIcon(
  ctx: CanvasRenderingContext2D,
  layout: OriginIconLayout,
) {
  if (!layout.channel) return

  imageCacheService.imageCache(LOGOS[layout.channel])(img => {
    ctx.drawImage(
      img,
      layout.iconX,
      layout.iconY,
      layout.iconSize,
      layout.iconSize,
    )
  })
}
